package com.schoolconnect.app.repository.config

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.AcademicYearWithSchoolResponse
import com.schoolconnect.app.datasource.network.response.SchoolConfigListItemResponse
import com.schoolconnect.app.datasource.network.response.SchoolConfigurationResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.datasource.network.request.AttendanceThresholdCreateRequest
import com.schoolconnect.app.datasource.network.request.ExamGradeThresholdCreateRequest
import com.schoolconnect.app.datasource.network.request.SchoolConfigCalendarUpdateRequest
import com.schoolconnect.app.datasource.network.request.SchoolConfigFeatureUpdateRequest
import com.schoolconnect.app.datasource.network.request.SchoolConfigGeneralUpdateRequest
import com.schoolconnect.app.datasource.network.request.SchoolFeatureConfigCreateRequest
import com.schoolconnect.app.datasource.network.request.SchoolHolidayCreateRequest
import com.schoolconnect.app.feature.schoolconfig.FeatureDraft
import com.schoolconnect.app.feature.schoolconfig.HolidayDraft
import com.schoolconnect.app.feature.schoolconfig.AcademicYearOption
import com.schoolconnect.app.feature.schoolconfig.SchoolConfigEditData
import com.schoolconnect.app.feature.schoolconfig.ThresholdDraft
import com.schoolconnect.app.feature.common.PageRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope

object ConfigRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ConfigRepository"
    private data class PageKey(
        val schoolId: String?,
        val academicYear: String?,
        val searchQuery: String?,
        val isActive: Boolean?,
        val pageIndex: Int,
        val pageSize: Int,
        val sortBy: String,
        val order: String
    )
    private data class CachedPage(val key: PageKey, val items: List<SchoolConfigListItemResponse>)
    private var cachedPage: CachedPage? = null
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    suspend fun loadSchoolConfigPage(
        schoolId: String? = null,
        academicYear: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String = "school_name",
        order: String = "asc",
        searchQuery: String? = null,
        isActive: Boolean? = null,
        forceRemote: Boolean = false
    ): Result<List<SchoolConfigListItemResponse>> {
        val request = PageRequest(pageIndex, pageSize)
        val normalizedSearch = searchQuery?.trim()?.takeIf { it.isNotBlank() }
        val key = PageKey(
            schoolId?.trim()?.takeIf { it.isNotBlank() },
            academicYear?.trim()?.takeIf { it.isNotBlank() },
            normalizedSearch,
            isActive,
            request.safePageIndex,
            request.safePageSize,
            sortBy.trim().ifBlank { "school_name" },
            order.trim().lowercase().takeIf { it == "asc" || it == "desc" } ?: "asc"
        )
        cachedPage?.takeIf { !forceRemote && it.key == key }?.let {
            scLog("$TAG loadSchoolConfigPage cacheHit pageIndex=${key.pageIndex} pageSize=${key.pageSize} total=${it.items.serverTotalCountOrNull() ?: "<unknown>"}")
            return returnCachedAndRefresh(
                scope = scope,
                tag = TAG,
                operation = "loadSchoolConfigPage",
                context = "pageIndex=${key.pageIndex} pageSize=${key.pageSize}",
                cached = it.items,
                refresh = {
                    loadSchoolConfigPage(
                        schoolId,
                        academicYear,
                        key.pageIndex,
                        key.pageSize,
                        key.sortBy,
                        key.order,
                        key.searchQuery,
                        key.isActive,
                        forceRemote = true
                    )
                }
            )
        }
        scLog("$TAG loadSchoolConfigPage start school=${key.schoolId ?: "<any>"} pageIndex=${key.pageIndex} pageSize=${key.pageSize} isActive=${key.isActive?.toString() ?: "<any>"}")
        return KtorApiClient.getSchoolConfigs(
            key.schoolId,
            key.academicYear,
            request.safePageSize,
            request.offset,
            key.sortBy,
            key.order,
            key.isActive
        ).map { items ->
            val deduped = items.distinctBy { it.schoolId ?: it.id }
            val serverTotal = items.serverTotalCountOrNull()
            // Join rows can inflate server count; after distinct, prefer a total that matches unique schools.
            val total = when {
                serverTotal == null -> deduped.size
                items.size < key.pageSize || items.size >= serverTotal -> deduped.size
                else -> (serverTotal - (items.size - deduped.size)).coerceAtLeast(deduped.size)
            }
            val normalized = deduped.withServerTotalCount(total)
            cachedPage = CachedPage(key, normalized)
            scLog("$TAG loadSchoolConfigPage complete count=${normalized.size} total=${normalized.serverTotalCountOrNull() ?: "<unknown>"}")
            normalized
        }.onFailure { throwable -> scLog("$TAG loadSchoolConfigPage failure pageIndex=${key.pageIndex} pageSize=${key.pageSize}", throwable) }
    }

    fun invalidateCaches() { cachedPage = null }

    suspend fun loadSchoolConfig(
        schoolId: String,
        academicYear: String?,
        academicYearId: String? = null,
        includeAcademicYearOptions: Boolean = true
    ): Result<SchoolConfigEditData> = coroutineScope {
        // Omit year on first open so the API resolves Current, else nearest Upcoming.
        // Fetch config, academic years, and weekly holiday in parallel — sequential calls made open feel slow.
        val configDeferred = async {
            KtorApiClient.getSchoolConfig(schoolId, academicYear, academicYearId)
        }
        val academicYearsDeferred = async {
            if (!includeAcademicYearOptions) {
                Result.success(emptyList<AcademicYearWithSchoolResponse>())
            } else {
                KtorApiClient.getAcademicYearsWithSchoolName(
                    schoolId = schoolId,
                    sortBy = "start_date",
                    order = "desc"
                )
            }
        }
        val weeklyHolidayDeferred = async {
            KtorApiClient.getWeeklyHolidayConfigs(schoolId)
        }

        val response = configDeferred.await().getOrElse { return@coroutineScope Result.failure(it) }
        val academicYearRows = academicYearsDeferred.await().getOrElse {
            return@coroutineScope Result.failure(
                IllegalStateException("Unable to load academic years for this school: ${it.message.orEmpty()}")
            )
        }
        val weeklyHolidayResult = weeklyHolidayDeferred.await()

        val academicYears = academicYearRows.mapNotNull { year ->
            val id = year.id?.trim().orEmpty()
            val label = year.label?.trim().orEmpty()
            id.takeIf { it.isNotBlank() }?.let {
                AcademicYearOption(
                    id = it,
                    label = label.ifBlank { it },
                    isCurrent = year.lifecycle.equals("Current", ignoreCase = true)
                )
            }
        }.distinctBy { it.id }
        val weeklyHoliday = weeklyHolidayResult.getOrNull()?.firstOrNull { it.isActive != false }
        if (weeklyHolidayResult.isFailure) {
            scLog(
                "$TAG weekly holiday config unavailable; continuing with Sunday default school=${schoolId.trim()}",
                weeklyHolidayResult.exceptionOrNull()
            )
        }
        val responseYearId = response.academicYearId?.trim()?.takeIf { it.isNotBlank() }
        val selectedYear = academicYears.firstOrNull { it.id == academicYearId }
            ?: academicYears.firstOrNull { it.id == responseYearId }
            ?: academicYears.firstOrNull { it.label == response.academicYear }
            ?: academicYears.firstOrNull { it.isCurrent }
            ?: academicYears.firstOrNull()
        runCatching {
            response.schoolId?.trim()?.takeIf { it.isNotBlank() }?.let { returnedSchoolId ->
                check(returnedSchoolId == schoolId.trim()) {
                    "School configuration belongs to a different school. Expected ${schoolId.trim()} but received $returnedSchoolId."
                }
            }
            response.toEditData(
                weeklyOffDay = weeklyHoliday?.dayOfWeek?.toDayLabel() ?: "Sunday",
                weeklyHolidayApiAvailable = weeklyHolidayResult.isSuccess,
                academicYearId = selectedYear?.id ?: responseYearId ?: academicYearId,
                academicYears = academicYears
            )
        }
    }

    suspend fun downloadHolidayTemplate(): Result<ByteArray> =
        KtorApiClient.downloadSchoolHolidayTemplate()

    suspend fun importHolidays(schoolId: String, fileName: String, bytes: ByteArray, academicYearId: String? = null): Result<String> =
        KtorApiClient.importSchoolHolidays(schoolId, fileName, bytes, academicYearId).map { response ->
            val imported = response.imported ?: 0
            val skipped = response.skipped ?: 0
            "Imported $imported holiday${if (imported == 1) "" else "s"}; skipped $skipped."
        }.onSuccess { invalidateCaches() }

    suspend fun saveSchoolConfig(schoolId: String, draft: SchoolConfigEditData): Result<Unit> {
        val general = runCatching {
            SchoolConfigGeneralUpdateRequest(
                attendanceThresholds = draft.attendance.mapIndexed { index, row ->
                    AttendanceThresholdCreateRequest(row.level.required("attendance level"), row.minimum.percent(), row.maximum.percent(), index)
                },
                examGradeThresholds = draft.exams.mapIndexed { _, row ->
                    ExamGradeThresholdCreateRequest(row.level.required("performance level"), row.grade.required("grade"), row.minimum.percent(), row.maximum.percent())
                }
            )
        }.getOrElse { return Result.failure(it) }
        val calendar = runCatching {
            SchoolConfigCalendarUpdateRequest(draft.holidays.map {
                SchoolHolidayCreateRequest(toApiDate(it.date), it.name.required("holiday name"))
            })
        }.getOrElse { return Result.failure(it) }
        val features = SchoolConfigFeatureUpdateRequest(draft.features.map {
            SchoolFeatureConfigCreateRequest(it.key.required("feature key"), it.enabled)
        })

        KtorApiClient.updateSchoolConfigGeneral(schoolId, general, draft.academicYearId).getOrElse {
            return Result.failure(IllegalStateException("Unable to save general configuration: ${it.message.orEmpty()}"))
        }
        KtorApiClient.updateSchoolConfigCalendar(schoolId, calendar, draft.academicYearId).getOrElse {
            return Result.failure(IllegalStateException("Unable to save calendar configuration: ${it.message.orEmpty()}"))
        }
        val savedFeatures = KtorApiClient.updateSchoolConfigFeatures(schoolId, features, draft.academicYearId).getOrElse {
            return Result.failure(IllegalStateException("Unable to save feature configuration: ${it.message.orEmpty()}"))
        }
        val expectedFeatureKeys = draft.features.map { it.key.trim() }.filter { it.isNotBlank() }.toSet()
        val returnedFeatureKeys = savedFeatures.mapNotNull { it.featureKey?.trim()?.takeIf(String::isNotBlank) }.toSet()
        if (expectedFeatureKeys.isNotEmpty() && !returnedFeatureKeys.containsAll(expectedFeatureKeys)) {
            return Result.failure(
                IllegalStateException(
                    "The feature API did not return all saved feature keys. Expected ${expectedFeatureKeys.size}, received ${returnedFeatureKeys.size}."
                )
            )
        }
        if (draft.weeklyHolidayApiAvailable) {
            KtorApiClient.upsertWeeklyHolidayConfig(schoolId = schoolId, dayOfWeek = draft.weeklyOffDay.toDayOfWeek()).getOrElse {
                return Result.failure(IllegalStateException("Unable to save weekly holiday configuration: ${it.message.orEmpty()}"))
            }
        } else {
            scLog("$TAG weekly holiday save skipped because the backend did not expose an active school context school=${schoolId.trim()}")
        }
        invalidateCaches()
        scLog("$TAG saveSchoolConfig complete school=${schoolId.trim()} attendance=${draft.attendance.size} holidays=${draft.holidays.size} features=${draft.features.size}")
        return Result.success(Unit)
    }

    private fun SchoolConfigurationResponse.toEditData(weeklyOffDay: String, weeklyHolidayApiAvailable: Boolean, academicYearId: String?, academicYears: List<AcademicYearOption>) = SchoolConfigEditData(
        academicYear = academicYear?.trim().orEmpty().ifBlank {
            academicYears.firstOrNull { it.id == academicYearId }?.label
                ?: academicYears.firstOrNull { it.isCurrent }?.label
                ?: academicYears.firstOrNull()?.label
                ?: ""
        },
        academicYearId = academicYearId
            ?: this.academicYearId?.trim()?.takeIf { it.isNotBlank() }
            ?: academicYears.firstOrNull { it.label == academicYear }?.id
            ?: academicYears.firstOrNull { it.isCurrent }?.id,
        academicYears = academicYears,
        weeklyOffDay = weeklyOffDay,
        attendance = attendanceThresholds.orEmpty().sortedBy { it.displayOrder ?: 0 }.map {
            ThresholdDraft(it.attendanceLevel.orEmpty(), (it.minPercentage ?: 0).toString(), (it.maxPercentage ?: 0).toString())
        },
        exams = examGradeThresholds.orEmpty().map {
            ThresholdDraft(it.performanceLevel.orEmpty(), (it.minPercentage ?: 0).toString(), (it.maxPercentage ?: 0).toString(), it.grade.orEmpty())
        },
        holidays = holidays.orEmpty().map { HolidayDraft(toDisplayDate(it.holidayDate.orEmpty()), it.holidayName.orEmpty()) },
        features = featureConfigs.orEmpty().map { FeatureDraft(it.featureKey.orEmpty(), it.isEnabled ?: false) },
        weeklyHolidayApiAvailable = weeklyHolidayApiAvailable
    )

    private fun String.required(label: String): String = trim().takeIf { it.isNotBlank() }
        ?: throw IllegalArgumentException("$label is required")

    private fun String.percent(): Int = trim().toIntOrNull()?.takeIf { it in 0..100 }
        ?: throw IllegalArgumentException("Percentage must be a whole number from 0 to 100")

    private fun String.toDayOfWeek(): Int = when (trim().lowercase()) {
        "sunday" -> 0
        "monday" -> 1
        "tuesday" -> 2
        "wednesday" -> 3
        "thursday" -> 4
        "friday" -> 5
        "saturday" -> 6
        else -> throw IllegalArgumentException("Weekly off day is not supported: $this")
    }

    private fun Int.toDayLabel(): String = when (this) {
        0 -> "Sunday"
        1 -> "Monday"
        2 -> "Tuesday"
        3 -> "Wednesday"
        4 -> "Thursday"
        5 -> "Friday"
        6 -> "Saturday"
        else -> "Sunday"
    }

    private fun toApiDate(value: String): String {
        val parts = value.trim().split(" ")
        if (parts.size == 3) {
            val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").indexOfFirst { it.equals(parts[1], true) } + 1
            if (parts[0].toIntOrNull() in 1..31 && month in 1..12 && parts[2].toIntOrNull() != null) {
                return "${parts[2]}-${month.toString().padStart(2, '0')}-${parts[0].padStart(2, '0')}"
            }
        }
        if (value.matches(Regex("\\d{4}-\\d{2}-\\d{2}"))) return value
        throw IllegalArgumentException("Holiday date must use DD MMM YYYY")
    }

    private fun toDisplayDate(value: String): String {
        val parts = value.substringBefore('T').split("-")
        if (parts.size == 3) {
            val month = parts[1].toIntOrNull()
            if (parts[0].length == 4 && month in 1..12) {
                val names = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
                return "${parts[2].padStart(2, '0')} ${names[month!! - 1]} ${parts[0]}"
            }
        }
        return value
    }
}
